export const BEVEL_DEFAULT_WIDTH = 0.2;

/** Human-facing mesh-edit actions; command validation remains authoritative. */
export function createMeshEditActions({
  meshSelection,
  bridge,
  isReady = () => false,
  refresh = () => {},
  reportError = () => {},
  onStateChange = () => {},
} = {}) {
  let bevelingEdge = false;
  let loopCuttingEdge = false;
  let welding = false;

  function bevelEdgeEligible() {
    return !bevelingEdge && isReady() && meshSelection.active &&
      meshSelection.elementType === 'edge' && meshSelection.ids.length === 1;
  }

  function loopCutEligible() {
    return !loopCuttingEdge && isReady() && meshSelection.active &&
      meshSelection.elementType === 'edge' && meshSelection.ids.length === 1;
  }

  async function bevelEdge() {
    if (!bevelEdgeEligible()) return;
    const id = meshSelection.meshId;
    const edgeId = meshSelection.ids[0];
    bevelingEdge = true;
    onStateChange();
    try {
      await bridge.execute(
        'bevelEdge',
        { id, edgeId, width: BEVEL_DEFAULT_WIDTH },
        { label: 'Bevel Edge' },
      );
      // A successful bevel invalidates the derived edge-id cache. Clear the
      // old selection first, then explicitly rebuild the view from the newly
      // committed topology. The mode/object guards preserve a newer user
      // selection if it changed while the command was in flight.
      if (meshSelection.active && meshSelection.meshId === id &&
          meshSelection.elementType === 'edge' && meshSelection.ids.length === 1 &&
          meshSelection.ids[0] === edgeId) {
        meshSelection.clear();
        refresh();
      }
    } catch (error) {
      reportError('bevelEdge', error);
    } finally {
      bevelingEdge = false;
      onStateChange();
    }
  }

  async function loopCut() {
    if (!loopCutEligible()) return;
    const id = meshSelection.meshId;
    const edgeId = meshSelection.ids[0];
    loopCuttingEdge = true;
    onStateChange();
    try {
      await bridge.execute('loopCut', { id, edgeId }, { label: 'Loop Cut' });
      if (meshSelection.active && meshSelection.meshId === id &&
          meshSelection.elementType === 'edge' && meshSelection.ids.length === 1 &&
          meshSelection.ids[0] === edgeId) {
        meshSelection.clear();
        refresh();
      }
    } catch (error) {
      reportError('loopCut', error);
    } finally {
      loopCuttingEdge = false;
      onStateChange();
    }
  }

  /**
   * Weld the mesh currently being edited.
   *
   * Whole-mesh rather than selection-scoped: welding is a repair for topology
   * that arrived with duplicated vertices, and a caller cannot usefully know
   * which subset of duplicates to keep. It needs Edit Mesh open only so there
   * is an unambiguous mesh to act on.
   */
  function weldEligible() {
    return !welding && isReady() && meshSelection.active && Boolean(meshSelection.meshId);
  }

  async function weld() {
    if (!weldEligible()) return;
    const id = meshSelection.meshId;
    welding = true;
    onStateChange();
    try {
      await bridge.execute('weldVertices', { id }, { label: 'Weld Vertices' });
      // Welding drops vertex ids, so any held element selection is stale.
      if (meshSelection.active && meshSelection.meshId === id) {
        meshSelection.clear();
        refresh();
      }
    } catch (error) {
      reportError('weldVertices', error);
    } finally {
      welding = false;
      onStateChange();
    }
  }

  return {
    bevelEdgeEligible,
    bevelEdge,
    loopCutEligible,
    loopCut,
    weldEligible,
    weld,
    get bevelingEdge() { return bevelingEdge; },
    get loopCuttingEdge() { return loopCuttingEdge; },
    get welding() { return welding; },
  };
}
